import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactDOM from "react-dom";
import { Main } from "../../MUIStyled/Main";
import { useMediaQuery } from "@mui/material";
import { DrawerHeader } from "../../MUIStyled/DrawerHeader";
import { getNewLegendContent } from "../Widgets/legendContent";
import { photoLocations } from "../../../../config/360photos";
import { stormScenarios } from "../../../../config/stormScenarios";
import { criticalFacilityInitialState } from "../../../../lib/Helpers/criticalFacilityState";
import { criticalFacilitiesAssetLayers } from "../../../../lib/Helpers/criticalFacilitiesURLs";
import CustomImgPopup from "../../Photos/CustomImgPopup";
import ToggleFloodRenderings from "../../Photos/ToggleFloodRenderings";
import ToggleCriticalFacilities from "../Widgets/ToggleCriticalFacilities";
import WebMap from "@arcgis/core/WebMap";
import MapView from "@arcgis/core/views/MapView";
import Popup from "@arcgis/core/widgets/Popup.js";
import LayerList from "@arcgis/core/widgets/LayerList";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import {
 addWidgets,
 createSlider,
 filterScenarios,
 updateLegendDiv,
 constructGallery,
 createSearchWidget,
 updateCurrSelection,
 initializeLayerList,
 updateResultantArray,
 generateSelectionText,
 addStormScenarioLayers,
 addPhotoLocationsLayer,
 addCriticalFacilitiesLayers,
} from "../../../../lib/Helpers/mapHelpers";
import "@photo-sphere-viewer/core/index.css";
import _ from "lodash";
import "../Maps.css";

// globals
let globalReductionState;
let photoLocationsLayerView;
let criticalFacilitiesWidget;
let toggleFloodRenderingsWidget;

const MapComponent = ({
 open,
 checked,
 mapExtent,
 setMapExtent,
 reductionState,
 criticalAssetsEnabled,
 setSearchResultsFeature,
 appContentOpenClassname,
 setCriticalAssetsEnabled,
}) => {
 // add a hook for the map component
 const mapRef = useRef();
 // access the critical facility component's DOM element
 const criticalFacilitiesID = useRef("cf-component");
 // 360 photo ref
 const toggleFloodRenderingsID = useRef("flood-renderings");

 // media query for small screens
 const isSmallScreen = useMediaQuery("(max-width: 460px)");

 // getters and setters
 const [theMap, setMap] = useState();
 const [mapView, setMapView] = useState();
 const [resultantArray, setResultantArray] = useState([]); // available options based on curr selection
 const [photoLocationsLayer, setPhotoLocationsLayer] = useState();
 const [floodRenderingsVisible, setFloodRenderingsVisible] = useState(true); // toggle flood rendering visiblity
 const [criticalFacilitiesLayers, setCriticalFacilitiesLayers] = useState({
  ...criticalFacilityInitialState,
 });

 // store variables
 const scenarioOne = useSelector((state) => state.scenarioOne.scenarioOne);
 const slr = useSelector((state) => state.scenarioOne.scenarioOne["slr"]);
 const surge = useSelector((state) => state.scenarioOne.scenarioOne["surge"]);
 const rainfall = useSelector(
  (state) => state.scenarioOne.scenarioOne["rainfall"]
 );
 const groundwater = useSelector(
  (state) => state.scenarioOne.scenarioOne["groundwater"]
 );

 // adaptationSelection
 useEffect(() => {
  const isAdaptation = true;
  updateResultantArray(
   reductionState["selectedAdaption"],
   reductionState["availableSLR"],
   setResultantArray,
   isAdaptation
  );
 }, [reductionState["selectedAdaption"], reductionState["availableSLR"]]);

 // slrSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedSLR"],
   reductionState["availableRain"],
   setResultantArray
  );
 }, [reductionState["selectedSLR"], reductionState["availableRain"]]);

 // rainfallSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedRainfallAmount"],
   reductionState["availableStormSurges"],
   setResultantArray
  );
 }, [
  reductionState["selectedRainfallAmount"],
  reductionState["availableStormSurges"],
 ]);

 // stormSurgeSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedStormSurge"],
   reductionState["availableGroundwater"],
   setResultantArray
  );
 }, [
  reductionState["selectedStormSurge"],
  reductionState["availableGroundwater"],
 ]);

 // groundwaterSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedGroundwaterConditions"],
   reductionState["finalSelection"],
   setResultantArray
  );
 }, [
  reductionState["selectedGroundwaterConditions"],
  reductionState["finalSelection"],
 ]);

 // set global reduction state variable...
 useEffect(() => {
  globalReductionState = { ...reductionState };
 }, [reductionState]);

 // toggle critical facility layers on and off
 const handleCriticalFacilityLayerChange = (layer, isVisible) => {
  if (!!layer) {
   layer.visible = isVisible;
  }
 };

 // create a popup template
 const getImage = (title) => {
  const popupTemplate = {
   title: `${title}`,
   content: (event) => {
    const container = document.createElement("div");
    container.className = "photo-360-container";

    const locationTitle = event.graphic.attributes["Name"];
    const reactContainer = document.createElement("div");

    // filter scenarios and construct the gallery
    const filteredScenarios = filterScenarios(scenarioOne, stormScenarios);
    const gallery = constructGallery(
     locationTitle,
     filteredScenarios,
     photoLocations
    );

    // render the CustomImgPopup component into the reactContainer
    ReactDOM.render(
     <CustomImgPopup feature={event.graphic} newSRC={gallery} />,
     reactContainer
    );

    container.appendChild(reactContainer);
    return container;
   },
  };

  return popupTemplate;
 };

 // load the map, layers, and widgets
 useEffect(() => {
  if (mapRef.current) {
   const map = new WebMap({
    id: "f0157cb1f68e495a8cb0cc1e3b50fa7b",
    basemap: "gray-vector",
    popup: new Popup({
     defaultPopupTemplateEnabled: true,
    }),
   });
   setMap(map);

   const mapView = new MapView({
    container: mapRef.current,
    map: map,
    center: [-80.15, 26.15],
    zoom: 11,
    extent: mapExtent,
    popup: new Popup({
     defaultPopupTemplateEnabled: true,
    }),
   });
   setMapView(mapView);

   let searchWidget; // define searchWidget in the outer scope

   mapView.when(() => {
    // add layers
    addStormScenarioLayers(map, stormScenarios);
    addCriticalFacilitiesLayers(
     map,
     criticalFacilitiesAssetLayers,
     setCriticalFacilitiesLayers
    );
    addPhotoLocationsLayer(map, setPhotoLocationsLayer);

    // add opacity slider
    const slider = createSlider(mapView);

    // loop through the layers
    const layerList = new LayerList({
     view: mapView,
     listItemCreatedFunction: (event) => {
      var itemView = event.item; // layer-view of selection
      itemView.hidden = true;
      // olny show visible layers in the layerlist
      itemView.watch("visible", (event) => {
       layerList.operationalItems.forEach((layerView) => {
        if (
         layerView.title === globalReductionState?.finalSelection[0]?.["title"] // need global state variable here
        ) {
         layerView.hidden = false;
         // retain the transparency when switching between scenarios
         layerView.layer.opacity = slider.values[0];
         //  change opacity of visible layer
         reactiveUtils.watch(
          () => slider.values.map((value) => value),
          (values) => (layerView.layer.opacity = values[0])
         );
        } else {
         layerView.hidden = true;
        }
       });
      });
     },
    });

    // add search widget
    searchWidget = createSearchWidget(mapView, setSearchResultsFeature);

    // add custom widgets
    criticalFacilitiesWidget = document.getElementById(
     criticalFacilitiesID.current
    );
    toggleFloodRenderingsWidget = document.getElementById(
     toggleFloodRenderingsID.current
    );
    mapView.ui.add(criticalFacilitiesWidget, "top-right");
    mapView.ui.add(toggleFloodRenderingsWidget, "top-right");

    const isMap1 = true; // flag to indicate this is Map 1

    // add other widgets to the map view
    addWidgets(mapView, checked, "legendDiv", "currSelection", isMap1);
   });

   return () => {
    // cleanup widgets and DOM elements
    const searchWidgetBox = document.getElementById("searchWidgetBox");
    if (searchWidgetBox) {
     const newSearchWidget = document.createElement("div");
     newSearchWidget.id = "searchWidget";
     newSearchWidget.className = "search-widget-container";
     searchWidgetBox.appendChild(newSearchWidget);
    }

    if (searchWidget) searchWidget.destroy();

    const cfWidget = document.getElementById("cf-component");
    if (cfWidget && typeof cfWidget === HTMLElement && checked) {
     cfWidget?.destroy();
    }

    const frWidget = document.getElementById("flood-renderings");
    if (frWidget && typeof frWidget === HTMLElement && checked) {
     frWidget?.destroy();
    }
   };
  }
 }, []); // runs once

 // show the citical facilities layers - no login required
 useEffect(() => {
  if (!!mapRef.current && !!theMap) {
   if (!!criticalAssetsEnabled) {
    Object.keys(criticalFacilityInitialState)?.forEach((stateVar) => {
     if (!!criticalFacilitiesLayers[stateVar]) {
      // add the map layers
      theMap.add(criticalFacilitiesLayers[stateVar]);
      // turn layers on
      handleCriticalFacilityLayerChange(
       criticalFacilitiesLayers[stateVar],
       true
      );
     }
    });
   }
  }
 }, [criticalAssetsEnabled, theMap]);

 // match the extents for map 1 and map 2
 useEffect(() => {
  let firstRun = true;
  if (!!theMap) {
   // look for when a boolean property becomes true (equivalent to watchUtils.whenTrue())
   reactiveUtils.when(
    () => mapView?.stationary === true,
    async () => {
     if (firstRun) {
      firstRun = false;
     } else {
      setMapExtent(mapView.extent); // set the store with the new extent information
     }
    }
   );
  }
 }, [theMap]);

 // match the extents for map 1 and map 2
 useEffect(() => {
  if (!!theMap) {
   if (
    !!mapView &&
    !!mapView.extent &&
    !!!mapView.extent.equals(mapExtent) &&
    !!checked
   ) {
    mapView.extent = mapExtent;
   }
  }
 }, [mapExtent, theMap]);

 // update the current selection box/widget
 useEffect(() => {
  if (!!mapRef.current && !!mapView) {
   const currSelectionBox = document.querySelectorAll("#currSelection");
   const selectionText = generateSelectionText(reductionState, slr, rainfall);
   updateCurrSelection(currSelectionBox, selectionText);
  }
 }, [
  theMap,
  rainfall,
  slr,
  reductionState["selectedAdaptation"],
  reductionState["selectedSLR"],
  reductionState["selectedRainfallAmount"],
  reductionState["selectedGroundwaterConditions"],
  reductionState["selectedStormSurge"],
 ]);

 // update the legend widget
 useEffect(() => {
  if (mapRef.current && mapView) {
   const legendDivBox = document.querySelectorAll("#legendDiv");
   const legendContent = getNewLegendContent(resultantArray);
   updateLegendDiv(legendDivBox, legendContent);
  }
 }, [mapView, resultantArray]);

 // change the layer list when the resultant array changes
 useEffect(() => {
  if (mapRef.current && mapView && photoLocationsLayer) {
   reactiveUtils
    .whenOnce(() => mapView.ready)
    .then(() => {
     initializeLayerList(
      mapView,
      resultantArray,
      floodRenderingsVisible,
      (layerView) => {
       photoLocationsLayerView = layerView; // set global layer variable
      }
     );
    });
  }
 }, [resultantArray, mapView, photoLocationsLayer, floodRenderingsVisible]);

 // on click, show critical facility/360 photo popups
 useEffect(() => {
  if (!!mapView && !!theMap && !!mapRef.current) {
   const handleClick = (event) => {
    mapView.hitTest(event).then((response) => {
     response.results
      .filter((result) => {
       const title = result?.layer?.title || "";
       return title.includes("360") || title.includes("Critical");
      })
      .forEach((result) => {
       if (result.layer.title.includes("360")) {
        const title = result.graphic.attributes.Name;
        result.graphic.layer.popupTemplate = getImage(title); // create a popup template to show 360 photos
       } else if (result.layer.title.includes("Critical")) {
        const newTitle = result.layer.defaultPopupTemplate.title.replace(
         "Broward County Critical Assets - ",
         ""
        );
        result.layer.defaultPopupTemplate.title = newTitle;
       }
      });
    });
   };

   mapView.on("click", handleClick);

   //  return () => {
   //   mapView.off("click", handleClick); // cleanup the event listener
   //  };
  }
 }, [theMap, mapView]);

 const iOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

 return (
  <>
   {!!iOS === true || !!isSmallScreen ? (
    <Main open={open} className="app-content-ios">
     <DrawerHeader />
     <div className="webmap1">
      <div className={`webmap`} ref={mapRef} />
     </div>
    </Main>
   ) : (
    <Main
     className={
      open === true ? `${appContentOpenClassname}` : "app-content-closed"
     }
     open={open}
    >
     <DrawerHeader />
     <div className="webmap1">
      <div className={`webmap`} ref={mapRef} />
      {!!Object.values(criticalFacilitiesLayers)[0] && (
       <div ref={criticalFacilitiesID}>
        <ToggleCriticalFacilities
         id={criticalFacilitiesID.current}
         handleCriticalFacilityLayerChange={handleCriticalFacilityLayerChange}
         criticalAssetsEnabled={criticalAssetsEnabled}
         setCriticalAssetsEnabled={setCriticalAssetsEnabled}
         criticalFacilitiesLayers={criticalFacilitiesLayers}
        />
        <ToggleFloodRenderings
         id={toggleFloodRenderingsID.current}
         photoLocationsLayer={photoLocationsLayer}
         floodRenderingsVisible={floodRenderingsVisible}
         setFloodRenderingsVisible={setFloodRenderingsVisible}
        />
       </div>
      )}
     </div>
    </Main>
   )}
  </>
 );
};

export default MapComponent;
